import 'client-only';

import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { RegistrationEligibilityState } from '@/atoms/eligibilityForTreatment/registrationEligibilityState';
import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useNavigation } from '@/components/flow/hooks/useNavigation';
import { CHECKOUT_MODES } from '@/components/flow/steps/checkout/types';
import { useCheckoutMode } from '@/components/product-selection/hooks/useCheckoutMode';
import { useRouter } from '@/hooks/useRouter';
import {
  deleteProductEligibility,
  deleteRegistrationEligibility,
} from '@/services/eligibilityService/actions';
import { type IneligibleReasonType } from '@/types/eligibility/eligibility';
import { urls } from '@/utils/urls';

const useCheckout = () => {
  const mode = useCheckoutMode();
  const { treatmentCategory } = useFlowContext();
  const { onNext } = useNavigation();
  const router = useRouter();
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  const updateIneligibleReasonAtom = useSetAtom(
    RegistrationEligibilityState.ineligibleReasonAtom,
  );

  const onSuccess = useCallback(() => {
    Promise.all([deleteRegistrationEligibility(), deleteProductEligibility()]);

    if (mode === CHECKOUT_MODES.Cart) {
      setFlashMessages([
        ...flashMessages,
        {
          children: 'Order confirmed!',
          state: 'success',
          autoDismiss: false,
        },
      ]);
    }

    onNext();
  }, [onNext, mode, flashMessages, setFlashMessages]);

  const onIneligible = useCallback(
    (ineligibleReason: IneligibleReasonType) => {
      updateIneligibleReasonAtom(ineligibleReason.userMessage);
      return router.push(urls.app.ineligible(treatmentCategory));
    },
    [router, treatmentCategory, updateIneligibleReasonAtom],
  );

  return { onSuccess, onIneligible };
};

export { useCheckout };
